import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, FileText, Package, Wrench as WrenchIcon,
    UserCircle, LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/conta", label: "Visão geral", icon: LayoutDashboard, exact: true },
    { to: "/conta/pedidos", label: "Pedidos", icon: FileText },
    { to: "/conta/equipamentos", label: "Equipamentos", icon: Package },
    { to: "/conta/assistencia", label: "Assistência", icon: WrenchIcon },
    { to: "/conta/perfil", label: "Perfil", icon: UserCircle },
];

export default function ContaLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    function onLogout() {
        logout();
        navigate("/entrar");
    }

    const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

    return (
        <div className="min-h-screen bg-muted text-primary flex" data-testid="conta-shell">
            <aside className={cn(
                "fixed inset-y-0 left-0 z-40 w-72 bg-primary text-white flex flex-col transition-transform lg:translate-x-0",
                open ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                    <Logo testId="conta-sidebar-logo" size="sm" variant="white" />
                    <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} aria-label="Fechar">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary font-heading font-extrabold">
                        {initials}
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{user?.name || "Cliente"}</div>
                        <div className="text-[11px] text-white/60 truncate">{user?.email}</div>
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-secondary">
                            {user?.role === "customer" ? "Cliente proprietário" : "Prospect"}
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-6 space-y-1" data-testid="conta-nav">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to} to={item.to} end={item.exact}
                            data-testid={`conta-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) => cn(
                                "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/75 transition hover:bg-white/10 hover:text-white",
                                isActive && "bg-secondary text-primary font-bold hover:bg-yellow-300"
                            )}
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="px-3 py-4 border-t border-white/10 space-y-2">
                    <Link to="/" className="flex items-center gap-3 rounded-md px-3 py-2 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10">
                        <ExternalLink className="h-3.5 w-3.5" /> Voltar ao site
                    </Link>
                    <button onClick={onLogout} data-testid="conta-logout"
                        className="w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition">
                        <LogOut className="h-4 w-4" /> Terminar sessão
                    </button>
                </div>
            </aside>

            {open && (<div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />)}

            <div className="flex-1 lg:ml-72 flex flex-col min-w-0">
                <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-border">
                    <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                        <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Menu">
                            <Menu className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-lg font-bold">A minha conta</h1>
                        <div className="text-xs text-muted-foreground">{user?.email}</div>
                    </div>
                </header>
                <main className="flex-1 px-6 lg:px-10 py-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
